import axios from 'axios';
import {
  Organization,
  CognitoUser,
  CreateUserRequest,
  UpdateUserRequest,
  CreateOrganizationRequest,
  UserActivity,
  Invoice,
  InvoiceDraftRequest,
  InvoiceAction,
  OrgStripeStatus
} from '../types/admin';

const API_BASE_URL = 'https://in6zkeyaof.execute-api.us-east-1.amazonaws.com/dev';

const createAuthenticatedApi = (token?: string) => {
  return axios.create({
    baseURL: API_BASE_URL,
    headers: token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    } : {}
  });
};

// ==================== Organizations ====================

export const fetchOrganizations = async (token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.get<{ organizations: Organization[]; count: number }>('/admin/organizations');
  return response;
};

export const createOrganization = async (data: CreateOrganizationRequest, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.post<{ message: string; organization: Organization }>('/admin/organizations', data);
  return response;
};

export const deleteOrganization = async (organizationId: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.delete<{ message: string; organization_id: string }>(
    `/admin/organizations/${organizationId}`
  );
  return response;
};

// ==================== Users ====================

export const fetchCognitoUsers = async (organizationId?: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const url = organizationId 
    ? `/admin/users?organization_id=${organizationId}`
    : '/admin/users';
  const response = await api.get<{ users: CognitoUser[]; count: number; filtered_by_org: string | null }>(url);
  return response;
};

export const createCognitoUser = async (data: CreateUserRequest, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.post<{ message: string; user: any; temporary_password?: string }>('/admin/users', data);
  return response;
};

export const updateCognitoUser = async (userId: string, data: UpdateUserRequest, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.put<{ message: string; user_id: string; changes: string[] }>(
    `/admin/users/${userId}`,
    data
  );
  return response;
};

export const deleteCognitoUser = async (userId: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.delete<{ message: string; user_id: string; email: string }>(
    `/admin/users/${userId}`
  );
  return response;
};

export const fetchUserActivity = async (userId: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.get<{
    user: { user_id: string; email: string; organization_id: string };
    activities: UserActivity[];
    count: number;
    summary: any;
  }>(`/admin/users/${userId}/activity`);
  return response;
};


// ==================== Invoices ====================

export const fetchInvoices = async (
  token?: string,
  filters?: { organization_id?: string; case_id?: string; status?: string }
) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.get<{ invoices: Invoice[]; stripe: Record<string, OrgStripeStatus> }>(
    '/admin/invoices',
    { params: filters }
  );
  return response.data;
};

export const createInvoice = async (data: InvoiceDraftRequest, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.post<{ invoice: Invoice }>('/admin/invoices', data);
  return response.data.invoice;
};

export const updateInvoice = async (invoiceId: string, data: Partial<InvoiceDraftRequest>, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.put<{ invoice: Invoice }>(`/admin/invoices/${invoiceId}`, data);
  return response.data.invoice;
};

export const deleteInvoice = async (invoiceId: string, token?: string) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  await api.delete(`/admin/invoices/${invoiceId}`);
};

export const invoiceAction = async (
  invoiceId: string,
  action: InvoiceAction,
  token?: string,
  body?: { send_email?: boolean; method?: string; reference?: string }
) => {
  if (!token) {
    throw new Error('No auth token provided');
  }
  const api = createAuthenticatedApi(token);
  const response = await api.post<{ invoice: Invoice }>(`/admin/invoices/${invoiceId}/${action}`, body || {});
  return response.data.invoice;
};
